// src/components/company.jsx
// CompanyDetail modal + CompaniesList grid view.

import { useState, useEffect, useMemo, memo } from "react";
import * as HS from "../hubspot.js";
import { STAGE_COLORS } from "../constants.js";
import { Btn, Chip, FI, LinkBtn, Modal, Spin, ActivitySummary } from "./ui.jsx";
import { OwnerBadge, OwnerSelect } from "./owners.jsx";
import { EmailCompose, LogActivity } from "./activity.jsx";
import { ContactDetail, AddContactModal } from "./contact.jsx";

// Company type options shown when a rep creates a new company.
// `label` is what reps see; `value` is the raw HubSpot `type` property value.
// These map to the same buckets used by the Route Planner type filter.
export const COMPANY_TYPE_OPTIONS = [
  { label: "Mattress Customer",    value: "Gribetz/Porter" },
  { label: "Mattress Prospect",    value: "Gribetz/Porter Prospect" },
  { label: "Industrial Customer",  value: "IND Customer" },
  { label: "Industrial Prospect",  value: "PROSPECT" },
];

// US state options for the State dropdown. Two-letter code + full name.
const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","District of Columbia"],
];

// ── AddCompanyModal ──────────────────────────────────────────────────────────
// Form for sales reps + admins to create a new company. ALL fields required.
// Performs duplicate check (name + city + state) before saving; on duplicate,
// shows the existing company name and offers to open it instead.
//
// Save flow:
//   1. Validate all fields filled
//   2. Run HS.findDuplicateCompany() - if found, show inline error + "Open Existing" button
//   3. Otherwise call HS.createCompany() with the new rep's ownerId
//   4. onCreated() callback receives the new company so parent can refresh
export function AddCompanyModal({currentUser, onClose, onCreated, onOpenDuplicate}) {
  const [name,    setName]    = useState("");
  const [city,    setCity]    = useState("");
  const [state,   setState]   = useState("");
  const [zip,     setZip]     = useState("");
  const [phone,   setPhone]   = useState("");
  const [address, setAddress] = useState("");
  const [type,    setType]    = useState(COMPANY_TYPE_OPTIONS[0].value);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [duplicate, setDuplicate] = useState(null);  // matched existing company

  const allFilled = !!(name.trim() && city.trim() && state.trim() && zip.trim() && phone.trim() && address.trim() && type);

  const handleSave = async () => {
    setErr("");
    setDuplicate(null);
    if (!allFilled) {
      setErr("All fields are required.");
      return;
    }
    setSaving(true);
    try {
      // Duplicate check
      const dup = await HS.findDuplicateCompany(name, city, state);
      if (dup) {
        setDuplicate(dup);
        setErr("A company with this name already exists in this city.");
        setSaving(false);
        return;
      }
      const ownerId = currentUser?.hubspotOwnerId || null;
      const newCompany = await HS.createCompany(
        { name, city, state, zip, phone, address, type },
        ownerId,
      );
      onCreated(newCompany);
      onClose();
    } catch (e) {
      setErr("Could not save: " + e.message);
    }
    setSaving(false);
  };

  return (
    <Modal title="+ New Company" onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <FI label="Company Name *" value={name} onChange={setName} placeholder="e.g. Smith Mattress Co"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <FI label="City *" value={city} onChange={setCity} placeholder="Houston"/>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>State *</label>
            <select value={state} onChange={e=>setState(e.target.value)}
              style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}>
              <option value="">— Select —</option>
              {US_STATES.map(([code, fullName]) => <option key={code} value={code}>{code} — {fullName}</option>)}
            </select>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <FI label="ZIP *" value={zip} onChange={setZip} placeholder="77001"/>
          <FI label="Phone *" value={phone} onChange={setPhone} placeholder="(555) 123-4567"/>
        </div>
        <FI label="Street Address *" value={address} onChange={setAddress} placeholder="123 Main St"/>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Company Type *</label>
          <select value={type} onChange={e=>setType(e.target.value)}
            style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}>
            {COMPANY_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        {err && (
          <div style={{padding:"12px 14px",borderRadius:10,background:"#fdecea",border:"1px solid var(--red)",color:"var(--red)",fontSize:13}}>
            {err}
            {duplicate && (
              <div style={{marginTop:8,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontWeight:600}}>Existing: {duplicate.properties.name}</span>
                <button onClick={()=>{ onClose(); onOpenDuplicate?.(duplicate); }}
                  style={{background:"var(--brown)",color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>
                  Open Existing
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:6}}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={saving || !allFilled}>
            {saving ? <><Spin/> Saving...</> : "Save Company"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

export function CompanyDetail({company, users, currentUser, onClose, onUpdate}) {
  const [acts,  setActs]  = useState([]);
  const [loadA, setLoadA] = useState(true);
  const [showEmail, setShowEmail] = useState(null);
  const [showLog,   setShowLog]   = useState(false);
  const [logContact,setLogContact]= useState(null);
  const [coOwner,   setCoOwner]   = useState(company.ownerId);
  const [contacts,  setContacts]  = useState(company.contacts || []);
  const [deals,     setDeals]     = useState(company.deals || []);
  const [loadingCo, setLoadingCo] = useState(false);
  const [selContact,setSelContact]= useState(null);
  const [showAddContact, setShowAddContact] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled && (contacts.length === 0 || deals.length === 0)) {
        setLoadingCo(true);
        const [fc, fd] = await Promise.all([
          HS.getContactsForCompany(company.id).catch(() => []),
          HS.getDealsForCompany(company.id).catch(() => []),
        ]);
        if (!cancelled) {
          setContacts(fc);
          setDeals(fd);
          onUpdate({...company, contacts: fc, deals: fd});
        }
        setLoadingCo(false);
      }
      setLoadA(true);
      try {
        const arr = await HS.getActivitiesForCompany(company.id);
        if (!cancelled) setActs(Array.isArray(arr) ? arr : []);
      } catch {
        if (!cancelled) setActs([]);
      }
      if (!cancelled) setLoadA(false);
    })();
    return () => { cancelled = true; };
  }, [company.id]);

  const p = company.properties;
  const isAdmin = currentUser.isAdmin;

  return (
    <>
      <Modal title={p.name} onClose={onClose} wide>
        <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            <div style={{padding:"16px 18px",borderRadius:14,background:"var(--warm1)",border:"1px solid var(--warm2)"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:12}}>Company Info</div>
              {[
                ["📍 Location", p.city + ", " + p.state],
                ["📞 Phone", p.phone],
                ["🏷 Type", p.type],
                ["🗺 Address", p.address],
              ].map(([k,v]) => (
                <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0",borderBottom:"1px solid var(--warm2)"}}>
                  <span style={{color:"var(--muted)"}}>{k}</span>
                  <span style={{fontWeight:500,textAlign:"right",maxWidth:"65%"}}>{v || "-"}</span>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,padding:"8px 0",marginTop:2}}>
                <span style={{color:"var(--muted)"}}>👤 Owner</span>
                {isAdmin
                  ? <OwnerSelect value={coOwner} onChange={v => {setCoOwner(v); onUpdate({...company, ownerId: v});}} users={users}/>
                  : <OwnerBadge ownerId={coOwner} users={users}/>
                }
              </div>
            </div>

            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Contacts</div>
                <button onClick={()=>setShowAddContact(true)}
                  style={{padding:"5px 12px",borderRadius:8,border:"1px solid var(--brown)",background:"#fff",color:"var(--brown)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>
                  + Contact
                </button>
              </div>
              {(contacts || []).map(c => (
                <div key={c.id} style={{padding:"12px 14px",borderRadius:12,border:"1px solid var(--warm2)",background:"#fff",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:14,marginBottom:1}}>
                        <LinkBtn onClick={()=>setSelContact(c)}>{c.name}</LinkBtn>
                      </div>
                      <div style={{fontSize:12,color:"var(--muted)"}}>{c.role} · {c.email}</div>
                    </div>
                    <div style={{display:"flex",gap:5}}>
                      <button onClick={()=>setShowEmail(c)} style={{padding:"4px 9px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:11,color:"var(--brown)",cursor:"pointer"}}>✉</button>
                      <button onClick={()=>setLogContact(c)} style={{padding:"4px 9px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:11,color:"var(--brown)",cursor:"pointer"}}>📋</button>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
                    <span style={{color:"var(--muted)"}}>Owner:</span>
                    {isAdmin
                      ? <OwnerSelect value={c.ownerId || null} onChange={v => onUpdate({...company, contacts: contacts.map(x => x.id === c.id ? {...x, ownerId: v} : x)})} users={users}/>
                      : <OwnerBadge ownerId={c.ownerId} users={users}/>
                    }
                  </div>
                </div>
              ))}
              {(contacts || []).length === 0 && (
                <div style={{fontSize:13,color:"var(--muted)",fontStyle:"italic"}}>
                  {loadingCo ? <><Spin/> Loading contacts...</> : "No contacts"}
                </div>
              )}
            </div>

            <div>
              <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Deals</div>
              {(deals || []).length
                ? deals.map(d => (
                  <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:12,border:"1px solid var(--warm2)",background:"#fff",marginBottom:8}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:3}}>{d.name}</div>
                      <Chip label={d.stage} color={STAGE_COLORS[d.stage] || "#999"}/>
                    </div>
                    <div style={{fontWeight:700,color:"var(--brown)",fontFamily:"Barlow Condensed,sans-serif"}}>${d.amount?.toLocaleString()}</div>
                  </div>
                ))
                : <div style={{fontSize:13,color:"var(--muted)",fontStyle:"italic"}}>No deals yet</div>
              }
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Activity</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setShowEmail(contacts?.[0] || {email:"", name:""})} style={{padding:"5px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:11,color:"var(--brown)",cursor:"pointer"}}>✉ Email</button>
                <button onClick={()=>setShowLog(true)} style={{padding:"5px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:11,color:"var(--brown)",cursor:"pointer"}}>+ Log</button>
              </div>
            </div>
            <div style={{overflowY:"auto",maxHeight:360}}>
              {loadA
                ? <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}><Spin/></div>
                : acts.length === 0
                  ? <div style={{padding:"30px 0",textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>No recorded activities yet.</div>
                  : acts.map((a, i) => (
                    <div key={i} style={{display:"flex",gap:12,padding:"12px 0",borderBottom:"1px solid var(--warm1)"}}>
                      <div style={{width:32,height:32,borderRadius:99,background:"var(--warm1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>
                        {a.type === "note" ? "📝" : a.type === "call" ? "📞" : "✉"}
                      </div>
                      <div>
                        <div style={{fontSize:12,color:"var(--muted)",marginBottom:3}}>
                          {a.date} · <span style={{textTransform:"capitalize"}}>{a.type}</span>
                        </div>
                        <div style={{fontSize:13,lineHeight:1.5}}><ActivitySummary activity={a} fallback={a.summary}/></div>
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      </Modal>
      {showEmail && <EmailCompose defaultTo={showEmail.email} defaultName={showEmail.name} onClose={()=>setShowEmail(null)}/>}
      {showLog && <LogActivity company={company} onClose={()=>setShowLog(false)} onLogged={a=>setActs(p=>[{...a,fresh:true},...p])} currentUser={currentUser}/>}
      {logContact && <LogActivity company={company} contact={logContact} onClose={()=>setLogContact(null)} onLogged={a=>setActs(p=>[{...a,fresh:true},...p])} currentUser={currentUser}/>}
      {selContact && <ContactDetail contact={selContact} company={company} users={users} currentUser={currentUser} onClose={()=>setSelContact(null)} onOpenCompany={()=>setSelContact(null)}/>}
      {showAddContact && (
        <AddContactModal
          companies={[company]}
          defaultCompanyId={company.id}
          currentUser={currentUser}
          onClose={()=>setShowAddContact(false)}
          onCreated={newContact => {
            const next = [...contacts, newContact];
            setContacts(next);
            onUpdate({...company, contacts: next});
          }}
        />
      )}
    </>
  );
}

// Memoized card so unchanged companies don't re-render when ANY single
// company gets enriched. This is critical when 4,000+ cards are mounted.
const CompanyCard = memo(function CompanyCard({c, users, onClick}) {
  const dCt = c.deals?.length || 0;
  const cCt = c.contacts?.length || 0;
  const top = c.deals?.[0];
  return (
    <div onClick={onClick}
      style={{background:"#fff",borderRadius:16,padding:20,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",cursor:"pointer",transition:"box-shadow .2s,transform .2s"}}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="var(--sh-lg)";e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="var(--sh)";e.currentTarget.style.transform="none";}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div>
          <h3 style={{fontSize:16,marginBottom:2}}>{c.properties.name}</h3>
          <div style={{fontSize:12,color:"var(--muted)"}}>📍 {c.properties.city}, {c.properties.state}</div>
        </div>
        <Chip label={c.properties.type || "-"} color="#111111"/>
      </div>
      <div style={{display:"flex",gap:14,fontSize:12,color:"var(--muted)",marginBottom:8}}>
        <span>👤 {cCt} contact{cCt!==1?"s":""}</span>
        <span>💼 {dCt} deal{dCt!==1?"s":""}</span>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:top?10:0,fontSize:12}}>
        <span style={{color:"var(--muted)"}}>Owner:</span>
        <OwnerBadge ownerId={c.ownerId} users={users}/>
      </div>
      {top && (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)"}}>
          <span style={{fontSize:12}}>{top.name}</span>
          <Chip label={top.stage} color={STAGE_COLORS[top.stage] || "#999"}/>
        </div>
      )}
      <div style={{marginTop:10,fontSize:12,color:"var(--brown)",fontWeight:500}}>View details →</div>
    </div>
  );
});

const PAGE_SIZE = 50;

export function CompaniesList({companies, users, currentUser, loading, onUpdate, initialOpen}) {
  const [sel,    setSel]    = useState(initialOpen || null);
  const [search, setSearch] = useState("");
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [typeFilter, setTypeFilter] = useState(() => {
    try { return localStorage.getItem("crm_co_type_filter") || ""; } catch { return ""; }
  });
  const [stateFilter, setStateFilter] = useState(() => {
    try { return localStorage.getItem("crm_co_state_filter") || ""; } catch { return ""; }
  });
  const [page, setPage] = useState(0);

  useEffect(() => { if (initialOpen) setSel(initialOpen); }, [initialOpen]);

  // Reset to page 0 whenever filters/search change
  useEffect(() => { setPage(0); }, [search, typeFilter, stateFilter]);

  const setTypeFilterPersist = (v) => {
    setTypeFilter(v);
    try { localStorage.setItem("crm_co_type_filter", v); } catch {}
  };
  const setStateFilterPersist = (v) => {
    setStateFilter(v);
    try { localStorage.setItem("crm_co_state_filter", v); } catch {}
  };
  const clearAllFilters = () => {
    setTypeFilterPersist("");
    setStateFilterPersist("");
  };

  // Build dropdown counts from full list -- memoized so we don't recompute on
  // every keystroke when only `search` changed.
  const {typeCounts, sortedTypes, stateCounts, sortedStates} = useMemo(() => {
    const tc = {}, sc = {};
    for (const c of companies) {
      const t = c.properties.type || "(no type)";
      tc[t] = (tc[t] || 0) + 1;
      const s = (c.properties.state || "").trim() || "(no state)";
      sc[s] = (sc[s] || 0) + 1;
    }
    return {
      typeCounts: tc,
      sortedTypes: Object.keys(tc).sort((a,b)=>a.localeCompare(b)),
      stateCounts: sc,
      sortedStates: Object.keys(sc).sort((a,b)=>a.localeCompare(b)),
    };
  }, [companies]);

  // Filter pipeline -- memoized
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return companies.filter(c => {
      if (typeFilter && (c.properties.type || "(no type)") !== typeFilter) return false;
      if (stateFilter) {
        const s = (c.properties.state || "").trim() || "(no state)";
        if (s !== stateFilter) return false;
      }
      if (!q) return true;
      return c.properties.name.toLowerCase().includes(q) ||
             (c.properties.city || "").toLowerCase().includes(q);
    });
  }, [companies, search, typeFilter, stateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const anyFilter = typeFilter || stateFilter;

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"flex-end"}}>
        <div style={{flex:"1 1 240px",minWidth:200}}>
          <FI placeholder="🔍 Search by name or city..." value={search} onChange={setSearch}/>
        </div>
        <div style={{flex:"0 0 220px",minWidth:180,display:"flex",flexDirection:"column",gap:5}}>
          <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Filter by Type</label>
          <select value={typeFilter} onChange={e => setTypeFilterPersist(e.target.value)}
            style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid "+(typeFilter?"var(--brown)":"var(--warm2)"),background:typeFilter?"var(--warm1)":"#fff",fontSize:13,color:"var(--text)",outline:"none",fontFamily:"Barlow,sans-serif",cursor:"pointer"}}>
            <option value="">All types ({companies.length})</option>
            {sortedTypes.map(t => (
              <option key={t} value={t}>{t} ({typeCounts[t]})</option>
            ))}
          </select>
        </div>
        <div style={{flex:"0 0 160px",minWidth:140,display:"flex",flexDirection:"column",gap:5}}>
          <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Filter by State</label>
          <select value={stateFilter} onChange={e => setStateFilterPersist(e.target.value)}
            style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid "+(stateFilter?"var(--brown)":"var(--warm2)"),background:stateFilter?"var(--warm1)":"#fff",fontSize:13,color:"var(--text)",outline:"none",fontFamily:"Barlow,sans-serif",cursor:"pointer"}}>
            <option value="">All states</option>
            {sortedStates.map(s => (
              <option key={s} value={s}>{s} ({stateCounts[s]})</option>
            ))}
          </select>
        </div>
        {anyFilter && (
          <button onClick={clearAllFilters}
            style={{padding:"10px 16px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:12,color:"var(--muted)",cursor:"pointer",fontFamily:"Barlow,sans-serif",height:40,whiteSpace:"nowrap"}}
            title="Clear all filters">✕ Clear filters</button>
        )}
        <button onClick={()=>setShowAddCompany(true)}
          style={{padding:"10px 18px",borderRadius:10,border:"none",background:"var(--brown)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",height:40,whiteSpace:"nowrap",marginLeft:"auto"}}
          title="Add a new company">+ New Company</button>
      </div>

      {/* Active-filter banner */}
      {anyFilter && (
        <div style={{padding:"8px 14px",borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)",fontSize:12,color:"var(--muted)",marginBottom:14,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span>Showing</span>
          <strong style={{color:"var(--brown)"}}>{filtered.length}</strong>
          <span>of</span>
          <strong style={{color:"var(--brown)"}}>{companies.length}</strong>
          <span>companies</span>
          {typeFilter && <span style={{padding:"2px 10px",borderRadius:99,background:"var(--brown)",color:"#fff",fontWeight:600}}>type: {typeFilter}</span>}
          {stateFilter && <span style={{padding:"2px 10px",borderRadius:99,background:"var(--brown)",color:"#fff",fontWeight:600}}>state: {stateFilter}</span>}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:14}}>
        {loading
          ? <div style={{gridColumn:"1/-1",padding:60,textAlign:"center",color:"var(--muted)"}}><Spin/> Loading...</div>
          : filtered.length === 0 && companies.length > 0
            ? <div style={{gridColumn:"1/-1",padding:60,textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>
                No companies match your search and filters.
              </div>
            : pageItems.map(c => (
                <CompanyCard key={c.id} c={c} users={users} onClick={()=>setSel(c)}/>
              ))
        }
      </div>

      {/* Pagination strip -- only shows when there's more than one page */}
      {!loading && filtered.length > PAGE_SIZE && (
        <div style={{marginTop:24,display:"flex",justifyContent:"center",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setPage(0)} disabled={safePage === 0}
            style={{padding:"7px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage===0?"var(--warm1)":"#fff",fontSize:12,color:safePage===0?"var(--warm3)":"var(--brown)",cursor:safePage===0?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>« First</button>
          <button onClick={()=>setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{padding:"7px 14px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage===0?"var(--warm1)":"#fff",fontSize:12,color:safePage===0?"var(--warm3)":"var(--brown)",cursor:safePage===0?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>‹ Prev</button>
          <span style={{padding:"7px 16px",fontSize:12,color:"var(--muted)",fontWeight:600}}>
            Page <strong style={{color:"var(--brown)"}}>{safePage + 1}</strong> of <strong style={{color:"var(--brown)"}}>{totalPages}</strong>
            <span style={{marginLeft:10,color:"var(--warm3)"}}>· {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          </span>
          <button onClick={()=>setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1}
            style={{padding:"7px 14px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage >= totalPages - 1?"var(--warm1)":"#fff",fontSize:12,color:safePage >= totalPages - 1?"var(--warm3)":"var(--brown)",cursor:safePage >= totalPages - 1?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>Next ›</button>
          <button onClick={()=>setPage(totalPages - 1)} disabled={safePage >= totalPages - 1}
            style={{padding:"7px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:safePage >= totalPages - 1?"var(--warm1)":"#fff",fontSize:12,color:safePage >= totalPages - 1?"var(--warm3)":"var(--brown)",cursor:safePage >= totalPages - 1?"not-allowed":"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>Last »</button>
        </div>
      )}

      {sel && <CompanyDetail company={sel} users={users} currentUser={currentUser} onClose={()=>setSel(null)} onUpdate={u => {onUpdate(u); setSel(u);}}/>}
      {showAddCompany && (
        <AddCompanyModal
          currentUser={currentUser}
          onClose={()=>setShowAddCompany(false)}
          onCreated={newCo => {
            // Append to the parent's companies list and open the detail
            // so the rep can immediately add contacts/deals.
            onUpdate(newCo);
            setSel(newCo);
          }}
          onOpenDuplicate={dup => {
            // Find the duplicate in our local companies list (it should exist
            // since we sync from HubSpot regularly). If found, open its detail.
            // If not in local cache yet, we still open with HubSpot-fetched fields.
            const local = companies.find(c => c.id === dup.id);
            setSel(local || dup);
          }}
        />
      )}
    </div>
  );
}
